import React from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { Controller, useFormContext } from "react-hook-form";
import { TickIcon } from "@root/assets/admin";

interface Option {
  label: string;
  value: string;
}

interface CommonSelectFieldProps {
  name: string;
  control: any;
  label: string;
  error?: any;
  options: Option[];
  placeholder?: string;
  placement?: "top" | "bottom" | "left" | "right";
}

const CommonSelectField: React.FC<CommonSelectFieldProps> = ({
  name,
  control,
  label,
  options,
  placeholder = "Select...",
  placement = "bottom",
}) => {
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | undefined>(
    undefined
  );
  React.useEffect(() => {
    if (btnRef.current) {
      setMenuWidth(btnRef.current.offsetWidth);
    }
  }, [btnRef.current]);

  const {
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const errorMessage =
    typeof error === "object" && error !== null ? error.message : undefined;

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel>{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        rules={{ required: `${label} is required` }}
        render={({ field }) => (
          <Menu isLazy placement={placement}>
            {({ isOpen }) => (
              <>
                <MenuButton
                  as={Button}
                  ref={btnRef}
                  width="100%"
                  bg="#FFF"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={
                    !!error ? "#E53E3E" : isOpen ? "#1819B4" : "#CDD6DC"
                  }
                  textAlign="left"
                  fontWeight={field.value ? 600 : 400}
                  color="#2F2F34"
                  _focus={{
                    borderColor: !!error ? "#E53E3E" : "#1819B4",
                    boxShadow: "none",
                  }}
                  _active={{
                    borderColor: !!error ? "#E53E3E" : "#1819B4",
                    boxShadow: "none",
                  }}
                  _hover={{
                    borderColor: !!error ? "#E53E3E" : "#1819B4",
                  }}
                  rightIcon={<ChevronDownIcon />}
                  px={4}
                  py={2}
                >
                  {options.find((opt) => opt.value === field.value)?.label ||
                    placeholder}
                </MenuButton>
                <MenuList
                  width={menuWidth ? `${menuWidth}px` : undefined}
                  minWidth="220px"
                  bg="#FFF"
                  maxH="250px"
                  overflowY="auto"
                  boxShadow={"4px 4px 8px 0px #0000000F"}
                  p={0}
                >
                  {options.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => field.onChange(option.value)}
                      bg={field.value === option.value ? "gray.100" : "#FFF"}
                      color={
                        field.value === option.value ? "#1819B4" : "#2F2F34"
                      }
                      fontWeight={field.value === option.value ? 600 : 400}
                      _hover={{
                        bg: "gray.100",
                        borderColor: "#1819B4",
                      }}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      px={4}
                      py={2}
                    >
                      <span>{option.label}</span>
                      {field.value === option.value && (
                        <TickIcon style={{ marginLeft: 8, color: "#1819B4" }} />
                      )}
                    </MenuItem>
                  ))}
                </MenuList>
              </>
            )}
          </Menu>
        )}
      />
      <FormErrorMessage>
        {typeof errorMessage === "string" ? errorMessage : null}
      </FormErrorMessage>
    </FormControl>
  );
};

export default CommonSelectField;
